import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Pencil, Trash2, X, Check } from "lucide-react";
import { api } from "../lib/api";

/**
 * RecentPlanCard — single AI plan row used on the customer dashboard, the
 * preview-mode customer dashboard, and the AI Concierge "Recent Plans"
 * surface. Phase 43-B spec:
 *   - Compact, high-contrast surface (pure white on warm ivory background).
 *   - Three actions on every card: View Details / Edit / Delete.
 *   - Delete asks for confirmation, then removes from the list.
 *   - Edit lets the customer rename the plan inline (title only — itinerary
 *     stays frozen so the AI-generated structure can't be corrupted).
 *
 * Props:
 *   plan          — { plan_id, plan: { title, ... }, status, created_at, ... }
 *   onDeleted     — callback(plan_id) after a successful delete
 *   onUpdated     — callback(plan)     after a successful rename
 *   readOnly      — when true (preview mode), the Edit/Delete actions still
 *                   fire the API but the parent decides what to do with the
 *                   preview_blocked error (toast it).
 */
export default function RecentPlanCard({ plan, onDeleted, onUpdated, readOnly = false }) {
  const planId = plan?.plan_id;
  const planBody = plan?.plan || {};
  const title = planBody.title || "Untitled plan";
  const status = (plan?.status || planBody.status || "draft").toString();
  const guests = planBody.guests;
  const price = planBody.estimated_price ?? planBody.final_customer_estimate;
  const duration = planBody.duration;

  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const statusClass = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    planning: "bg-amber-50 text-amber-700 border-amber-200",
    confirmed: "bg-blue-50 text-blue-700 border-blue-200",
    completed: "bg-stone-100 text-stone-600 border-stone-200",
    saved: "bg-violet-50 text-violet-700 border-violet-200",
  }[status] || "bg-stone-50 text-stone-600 border-stone-200";

  const handleSave = async () => {
    if (!draftTitle.trim() || draftTitle.trim() === title) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setErrMsg("");
    try {
      const res = await api.patch(`/ai/plans/${planId}`, { title: draftTitle.trim() });
      setEditing(false);
      onUpdated?.(res.data);
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || "Could not save changes.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    setErrMsg("");
    try {
      await api.delete(`/ai/plans/${planId}`);
      onDeleted?.(planId);
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || "Could not delete plan.");
      setBusy(false);
    }
  };

  return (
    <article
      data-testid={`recent-plan-card-${planId}`}
      className="group rounded-2xl border border-stone-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(28,25,23,0.04)] transition hover:border-stone-300 hover:shadow-[0_4px_12px_rgba(28,25,23,0.06)]"
    >
      {/* Row 1: title + status */}
      <div className="flex items-start justify-between gap-3">
        {editing ? (
          <input
            data-testid={`recent-plan-edit-input-${planId}`}
            autoFocus
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") { setDraftTitle(title); setEditing(false); }
            }}
            className="flex-1 min-w-0 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-sm font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
            maxLength={140}
          />
        ) : (
          <h3 className="flex-1 min-w-0 truncate text-base font-semibold tracking-tight text-stone-900">
            {title}
          </h3>
        )}
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${statusClass}`}>
          {status}
        </span>
      </div>

      {/* Row 2: meta */}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-stone-600">
        {duration && <span>{duration}</span>}
        {duration && (guests || price) && <span className="text-stone-300">·</span>}
        {guests != null && <span>{guests} {guests === 1 ? "guest" : "guests"}</span>}
        {guests != null && price != null && <span className="text-stone-300">·</span>}
        {price != null && (
          <span className="font-medium text-stone-700">
            ${Number(price).toLocaleString()}
          </span>
        )}
      </div>

      {errMsg && (
        <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[12px] text-rose-700">
          {errMsg}
        </div>
      )}

      {/* Row 3: actions */}
      <div className="mt-3 flex items-center gap-2">
        {!editing && !confirmingDelete && (
          <>
            <Link
              to={`/customer/plans/${planId}`}
              data-testid={`recent-plan-view-${planId}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-[12px] font-medium text-stone-800 hover:bg-stone-50"
            >
              <Eye size={13} /> View Details
            </Link>
            <button
              type="button"
              data-testid={`recent-plan-edit-${planId}`}
              onClick={() => { setDraftTitle(title); setEditing(true); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-[12px] font-medium text-stone-700 hover:bg-stone-50"
            >
              <Pencil size={13} /> Edit
            </button>
            <button
              type="button"
              data-testid={`recent-plan-delete-${planId}`}
              onClick={() => setConfirmingDelete(true)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-[12px] font-medium text-rose-700 hover:bg-rose-50 hover:border-rose-200"
            >
              <Trash2 size={13} /> Delete
            </button>
          </>
        )}

        {editing && (
          <>
            <button
              type="button"
              disabled={busy}
              data-testid={`recent-plan-save-${planId}`}
              onClick={handleSave}
              className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-stone-800 disabled:opacity-60"
            >
              <Check size={13} /> Save
            </button>
            <button
              type="button"
              disabled={busy}
              data-testid={`recent-plan-cancel-edit-${planId}`}
              onClick={() => { setDraftTitle(title); setEditing(false); setErrMsg(""); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-[12px] font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-60"
            >
              <X size={13} /> Cancel
            </button>
          </>
        )}

        {confirmingDelete && (
          <div className="flex w-full items-center justify-between gap-2 rounded-xl bg-rose-50 px-3 py-2">
            <p className="text-[12.5px] text-rose-800">Delete this plan? This can&apos;t be undone.</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                data-testid={`recent-plan-confirm-delete-${planId}`}
                onClick={handleDelete}
                className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-rose-700 disabled:opacity-60"
              >
                <Trash2 size={13} /> Delete
              </button>
              <button
                type="button"
                disabled={busy}
                data-testid={`recent-plan-cancel-delete-${planId}`}
                onClick={() => { setConfirmingDelete(false); setErrMsg(""); }}
                className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-white px-3 py-1.5 text-[12px] font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {readOnly && <span className="sr-only">Preview Mode — actions are demonstrative only.</span>}
    </article>
  );
}
